import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MaterialTable from 'material-table';
import dayjs from 'dayjs';
import moment from 'moment/min/moment-with-locales';
import { api } from 'common';
import { formatCop as formatCopRaw } from 'common/src/other/currencyFormat';
import { Alert, Box, Chip, Grid, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import localization from '../Table/Localization';
import C2Dialog, { C2PrimaryButton, C2SecondaryButton, C2DangerButton } from '../C2Dialog';
import {
  C2,
  C2_FONT,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  C2TableCard,
  C2SectionHeading,
  C2AddButton,
  C2EmptyState,
  C2DocsChip,
  C2StatusChip,
  C2SummaryCard,
  C2ProgressBar,
  C2Switch,
} from '../Table/C2UserTable';

const QUICK_AMOUNTS = [5000, 10000, 20000, 50000];

const STATUS_UI = {
  active: { tone: 'success', label: 'Activa' },
  scheduled: { tone: 'violet', label: 'Programada' },
  paused: { tone: 'neutral', label: 'Pausada' },
  ended: { tone: 'danger', label: 'Finalizada' },
  exhausted: { tone: 'warning', label: 'Cupo agotado' },
};

const GRANT_STATUS = {
  done: { tone: 'success', label: 'Acreditado' },
  processing: { tone: 'warning', label: 'En proceso' },
  skipped: { tone: 'neutral', label: 'Sin cupo' },
  failed: { tone: 'danger', label: 'Fallido' },
};

const formatCop = (v) => formatCopRaw(Math.round(Number(v) || 0));
const digits = (v, max = 9) => String(v ?? '').replace(/\D/g, '').slice(0, max);

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontFamily: C2_FONT,
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: C2.border },
    '&:hover fieldset': { borderColor: C2.bright },
    '&.Mui-focused fieldset': { borderColor: C2.violet },
  },
  '& .MuiInputLabel-root': { fontFamily: C2_FONT, color: C2.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: C2.violet },
  '& .MuiFormHelperText-root': { fontFamily: C2_FONT },
};

const pillSx = (active) => ({
  fontFamily: C2_FONT,
  fontWeight: 700,
  borderRadius: '10px',
  border: `1px solid ${active ? C2.violet : C2.border}`,
  color: active ? C2.white : C2.violet,
  background: active ? `linear-gradient(90deg, ${C2.violet} 0%, ${C2.bright} 100%)` : C2.lavender,
  '&:hover': { background: active ? `linear-gradient(90deg, ${C2.violet} 0%, ${C2.bright} 100%)` : C2.border },
});

const emptyForm = () => ({
  name: 'Bienvenida conductores',
  amount: '10000',
  startAt: Date.now(),
  hasEnd: false,
  endAt: dayjs().add(30, 'day').endOf('day').valueOf(),
  maxGrants: '',
  active: true,
});

const toForm = (c) => ({
  id: c.id,
  name: c.name || '',
  amount: c.amount ? String(c.amount) : '',
  startAt: Number(c.startAt) || Date.now(),
  hasEnd: Number(c.endAt) > 0,
  endAt: Number(c.endAt) || dayjs().add(30, 'day').endOf('day').valueOf(),
  maxGrants: Number(c.maxGrants) > 0 ? String(c.maxGrants) : '',
  active: !!c.active,
});

function validate(form, grantedCount) {
  const e = {};
  const name = form.name.trim();
  const amount = Number(form.amount) || 0;
  const max = Number(form.maxGrants) || 0;
  if (name.length < 3) e.name = 'Escribe un nombre de al menos 3 caracteres.';
  if (amount <= 0) e.amount = 'Define el saldo a regalar.';
  else if (amount > 1000000) e.amount = 'El máximo por conductor es $1.000.000.';
  if (!form.startAt) e.startAt = 'Elige desde cuándo aplica.';
  if (form.hasEnd && (!form.endAt || form.endAt <= form.startAt)) e.endAt = 'La fecha de fin debe ser posterior al inicio.';
  if (max > 0 && max < grantedCount) e.maxGrants = `Ya hay ${grantedCount} beneficiarios; el cupo no puede ser menor.`;
  return e;
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.82rem', color: C2.muted }}>{label}</Typography>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', fontWeight: 700, color: C2.sidebar, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}

export default function WalletCampaignsPanel() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const profile = useSelector((s) => s.auth.profile);
  const users = useSelector((s) => s.usersdata.users);
  const canManage = api.canAuthorizeWalletCredit(profile);

  const [campaigns, setCampaigns] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [form, setForm] = useState(null);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [grantsFor, setGrantsFor] = useState(null);
  const [grants, setGrants] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => api.subscribeWalletCampaigns(setCampaigns, () => setCampaigns([])), []);

  const rows = useMemo(() => {
    const now = Date.now();
    return (campaigns || []).map((c) => ({ ...c, status: api.walletCampaignStatus(c, now) }));
  }, [campaigns]);

  const summary = useMemo(
    () => ({
      active: rows.filter((r) => r.status === 'active').length,
      drivers: rows.reduce((acc, r) => acc + (Number(r.grantedCount) || 0), 0),
      total: rows.reduce((acc, r) => acc + (Number(r.grantedTotal) || 0), 0),
    }),
    [rows]
  );

  const usersById = useMemo(() => {
    const map = {};
    (users || []).forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [users]);

  const editing = form?.id ? rows.find((r) => r.id === form.id) : null;
  const grantedCount = Number(editing?.grantedCount) || 0;
  const errors = form ? validate(form, grantedCount) : {};
  const hasErrors = Object.keys(errors).length > 0;
  const err = (k) => (touched ? errors[k] : undefined);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const openForm = (campaign) => {
    setTouched(false);
    setForm(campaign ? toForm(campaign) : emptyForm());
  };

  const handleSave = async () => {
    setTouched(true);
    if (hasErrors) return;
    setSaving(true);
    try {
      await api.saveWalletCampaign({
        id: form.id,
        name: form.name.trim(),
        audience: 'new_driver',
        amount: Number(form.amount),
        active: !!form.active,
        startAt: Number(form.startAt),
        endAt: form.hasEnd ? Number(form.endAt) : null,
        maxGrants: Number(form.maxGrants) > 0 ? Number(form.maxGrants) : null,
      });
      setNotice({ severity: 'success', msg: form.id ? 'Campaña actualizada.' : 'Campaña creada. Se aplicará a los conductores nuevos que queden aprobados.' });
      setForm(null);
    } catch (e) {
      setNotice({ severity: 'error', msg: 'No se pudo guardar la campaña. Solo el administrador principal puede hacerlo.' });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.setWalletCampaignActive(row.id, !row.active);
    } catch (e) {
      setNotice({ severity: 'error', msg: 'No se pudo cambiar el estado de la campaña.' });
    }
  };

  const openGrants = async (row) => {
    setGrantsFor(row);
    setGrants(null);
    try {
      setGrants(await api.fetchWalletCampaignGrants(row.id));
    } catch (e) {
      setGrants([]);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.deleteWalletCampaign(deleting.id);
      setNotice({ severity: 'success', msg: 'Campaña eliminada.' });
    } catch (e) {
      setNotice({ severity: 'error', msg: 'No se pudo eliminar la campaña.' });
    }
    setDeleting(null);
  };

  const columns = [
    {
      title: 'Campaña',
      field: 'name',
      render: (r) => (
        <Box sx={{ textAlign: 'left', minWidth: 170 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.9rem', color: C2.sidebar }}>{r.name}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.76rem', color: C2.muted }}>{api.WALLET_CAMPAIGN_AUDIENCES[r.audience] || 'Conductores nuevos'} · al quedar aprobados</Typography>
        </Box>
      ),
    },
    {
      title: 'Saldo por conductor',
      field: 'amount',
      render: (r) => <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '0.95rem', color: '#15803D', whiteSpace: 'nowrap' }}>+ {formatCop(r.amount)}</Typography>,
    },
    {
      title: 'Vigencia',
      field: 'startAt',
      render: (r) => (
        <Box sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.82rem', color: C2.sidebar }}>Desde {moment(r.startAt).format('ll')}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.76rem', color: C2.muted }}>{Number(r.endAt) > 0 ? `Hasta ${moment(r.endAt).format('ll')}` : 'Sin fecha de fin'}</Typography>
        </Box>
      ),
    },
    {
      title: 'Beneficiarios',
      field: 'grantedCount',
      render: (r) =>
        Number(r.maxGrants) > 0 ? (
          <C2ProgressBar used={Number(r.grantedCount) || 0} total={Number(r.maxGrants)} done={r.status === 'exhausted'} />
        ) : (
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.84rem', color: C2.dark, whiteSpace: 'nowrap' }}>
            <b>{Number(r.grantedCount) || 0}</b> · sin límite
          </Typography>
        ),
    },
    {
      title: 'Entregado',
      field: 'grantedTotal',
      render: (r) => <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar, whiteSpace: 'nowrap' }}>{formatCop(r.grantedTotal)}</Typography>,
    },
    {
      title: 'Estado',
      field: 'status',
      render: (r) => <C2StatusChip tone={STATUS_UI[r.status].tone} label={STATUS_UI[r.status].label} />,
    },
    {
      title: 'Activa',
      field: 'active',
      render: (r) => <C2Switch checked={!!r.active} disabled={!canManage || r.status === 'ended'} onClick={(e) => e.stopPropagation()} onChange={() => toggleActive(r)} />,
    },
  ];

  const actions = [
    {
      icon: () => <C2DocsChip icon={PeopleAltOutlinedIcon} label="Beneficiarios" />,
      tooltip: 'Ver beneficiarios',
      onClick: (e, row) => openGrants(row),
    },
    ...(canManage
      ? [
          {
            icon: () => <C2DocsChip icon={EditOutlinedIcon} label="Editar" />,
            tooltip: t('edit'),
            onClick: (e, row) => openForm(row),
          },
          (row) => ({
            icon: () => <DeleteOutlineRoundedIcon sx={{ color: '#B91C1C' }} />,
            tooltip: 'Eliminar (solo sin beneficiarios)',
            hidden: Number(row.grantedCount) > 0,
            onClick: (e, r) => setDeleting(r),
          }),
        ]
      : []),
  ];

  const toolbar = canManage ? c2Toolbar(<C2AddButton key="add" label="Crear campaña" onClick={() => openForm(null)} />) : c2Toolbar();

  const amountNum = Number(form?.amount) || 0;

  return (
    <>
      {notice ? (
        <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
          {notice.msg}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <C2SummaryCard icon={CampaignOutlinedIcon} label="Campañas activas" value={summary.active} caption="Aplicándose ahora mismo" accent="#16A34A" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <C2SummaryCard icon={GroupAddOutlinedIcon} label="Conductores beneficiados" value={summary.drivers} caption="En todas las campañas" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <C2SummaryCard icon={SavingsOutlinedIcon} label="Saldo entregado" value={formatCop(summary.total)} caption="Saldo de campaña acreditado" />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.75, mb: 2, borderRadius: '14px', backgroundColor: C2.lavender, border: `1px solid ${C2.border}` }}>
        {canManage ? <InfoOutlinedIcon sx={{ color: C2.violet, mt: '2px' }} /> : <LockOutlinedIcon sx={{ color: C2.violet, mt: '2px' }} />}
        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.84rem', color: C2.dark, lineHeight: 1.5 }}>
          El saldo se acredita automáticamente cuando un conductor registrado durante la campaña queda aprobado. <b>No se puede retirar</b>: solo sirve
          para pagar las comisiones de sus viajes, y <b>no genera bonos de referido</b>.
          {canManage ? '' : ' Solo el administrador principal puede crear o modificar campañas.'}
        </Typography>
      </Box>

      <C2TableCard>
        <C2SectionHeading title="Campañas de saldo" subtitle="Saldo de bienvenida para los conductores nuevos de Conecta2." />
        <MaterialTable
          title="Campañas de saldo"
          columns={columns}
          data={rows}
          isLoading={campaigns === null}
          style={c2TableStyle(isRTL)}
          components={toolbar}
          onRowClick={(evt, row) => setSelectedRow(row.tableData.id)}
          options={{ ...c2TableOptions(selectedRow), actionsColumnIndex: -1 }}
          localization={c2Localization(
            localization(t),
            <C2EmptyState
              icon={CampaignOutlinedIcon}
              title="Aún no hay campañas"
              message={canManage ? 'Crea una campaña, por ejemplo $10.000 de bienvenida para cada conductor nuevo aprobado.' : 'Cuando el administrador principal cree una campaña, aparecerá aquí.'}
            />
          )}
          actions={actions}
        />
      </C2TableCard>

      <C2Dialog
        open={!!form}
        onClose={() => (saving ? null : setForm(null))}
        title={form?.id ? 'Editar campaña' : 'Nueva campaña de saldo'}
        icon={CampaignOutlinedIcon}
        maxWidth={560}
        actions={
          <>
            <C2SecondaryButton onClick={() => setForm(null)} disabled={saving}>
              Cancelar
            </C2SecondaryButton>
            <C2PrimaryButton onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando…' : form?.id ? 'Guardar cambios' : 'Crear campaña'}
            </C2PrimaryButton>
          </>
        }
      >
        {form ? (
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nombre de la campaña" value={form.name} onChange={(e) => set('name')(e.target.value.slice(0, 60))} error={!!err('name')} helperText={err('name') || 'Se muestra en la notificación al conductor.'} sx={fieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Saldo por conductor (COP)"
                value={form.amount}
                onChange={(e) => set('amount')(digits(e.target.value, 7))}
                inputProps={{ inputMode: 'numeric' }}
                error={!!err('amount')}
                helperText={err('amount') || (amountNum > 0 ? formatCop(amountNum) : ' ')}
                sx={fieldSx}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {QUICK_AMOUNTS.map((v) => (
                  <Chip key={v} label={formatCop(v)} onClick={() => set('amount')(String(v))} sx={pillSx(amountNum === v)} />
                ))}
              </Box>
            </Grid>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Aplica desde"
                  value={dayjs(form.startAt)}
                  onChange={(v) => set('startAt')(v && v.isValid() ? v.valueOf() : null)}
                  slotProps={{ textField: { fullWidth: true, error: !!err('startAt'), helperText: err('startAt') || 'Conductores registrados desde esta fecha', sx: fieldSx } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {form.hasEnd ? (
                  <DateTimePicker
                    label="Hasta"
                    value={dayjs(form.endAt)}
                    onChange={(v) => set('endAt')(v && v.isValid() ? v.valueOf() : null)}
                    slotProps={{ textField: { fullWidth: true, error: !!err('endAt'), helperText: err('endAt') || ' ', sx: fieldSx } }}
                  />
                ) : (
                  <Box sx={{ height: 56, display: 'flex', alignItems: 'center', px: 1.5, borderRadius: '12px', border: `1px dashed ${C2.border}`, color: C2.muted, fontFamily: C2_FONT, fontSize: '0.88rem' }}>
                    Sin fecha de fin
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <C2Switch size="small" checked={form.hasEnd} onChange={(e) => set('hasEnd')(e.target.checked)} />
                  <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>Definir fecha de fin</Typography>
                </Box>
              </Grid>
            </LocalizationProvider>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cupo máximo de conductores"
                value={form.maxGrants}
                onChange={(e) => set('maxGrants')(digits(e.target.value, 6))}
                inputProps={{ inputMode: 'numeric' }}
                error={!!err('maxGrants')}
                helperText={err('maxGrants') || (Number(form.maxGrants) > 0 && amountNum > 0 ? `Presupuesto máx. ${formatCop(Number(form.maxGrants) * amountNum)}` : 'Vacío = sin límite')}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, borderRadius: '12px', border: `1px solid ${C2.border}`, backgroundColor: C2.bg }}>
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>Campaña activa</Typography>
                <C2Switch checked={form.active} onChange={(e) => set('active')(e.target.checked)} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 1.5, borderRadius: '12px', backgroundColor: C2.bg, border: `1px solid ${C2.border}` }}>
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', color: C2.dark, lineHeight: 1.5 }}>
                  Cada conductor que se registre desde el <b>{form.startAt ? moment(form.startAt).format('lll') : '—'}</b>
                  {form.hasEnd && form.endAt ? <> hasta el <b>{moment(form.endAt).format('lll')}</b></> : null} recibirá{' '}
                  <b>{amountNum > 0 ? formatCop(amountNum) : '—'}</b> de saldo no retirable al quedar aprobado
                  {Number(form.maxGrants) > 0 ? <>, hasta completar <b>{form.maxGrants}</b> conductores</> : null}.
                </Typography>
                {form.id ? <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: C2.muted, mt: 0.75 }}>Los cambios solo afectan a los conductores que se aprueben de aquí en adelante.</Typography> : null}
              </Box>
            </Grid>
            {touched && hasErrors ? (
              <Grid item xs={12}>
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: '#B91C1C' }}>Revisa los campos marcados en rojo.</Typography>
              </Grid>
            ) : null}
          </Grid>
        ) : null}
      </C2Dialog>

      <C2Dialog
        open={!!grantsFor}
        onClose={() => setGrantsFor(null)}
        title={grantsFor ? `Beneficiarios · ${grantsFor.name}` : 'Beneficiarios'}
        icon={PeopleAltOutlinedIcon}
        maxWidth={760}
        actions={<C2SecondaryButton onClick={() => setGrantsFor(null)}>Cerrar</C2SecondaryButton>}
      >
        <MaterialTable
          title="Beneficiarios"
          isLoading={grants === null}
          data={grants || []}
          style={c2TableStyle(isRTL)}
          options={{ ...c2TableOptions(null), pageSize: 10, pageSizeOptions: [10, 20, 50] }}
          columns={[
            { title: 'Fecha', field: 'date', render: (r) => (r.date ? moment(r.date).format('lll') : '—') },
            {
              title: 'Conductor',
              field: 'userName',
              render: (r) => {
                const u = usersById[r.uid];
                const name = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : r.userName;
                return (
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar }}>{name || '—'}</Typography>
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>{r.cityName || u?.cityName || ''}</Typography>
                  </Box>
                );
              },
            },
            { title: 'Monto', field: 'amount', render: (r) => formatCop(r.amount) },
            {
              title: 'Saldo de campaña hoy',
              field: 'uid',
              render: (r) => (usersById[r.uid] ? formatCop(api.getWalletPromoBalance(usersById[r.uid])) : '—'),
            },
            {
              title: 'Estado',
              field: 'status',
              render: (r) => {
                const s = GRANT_STATUS[r.status] || GRANT_STATUS.processing;
                return <C2StatusChip tone={s.tone} label={s.label} />;
              },
            },
          ]}
          localization={c2Localization(localization(t), <C2EmptyState icon={PeopleAltOutlinedIcon} title="Aún no hay beneficiarios" message="Los conductores aparecerán aquí cuando queden aprobados." />)}
        />
      </C2Dialog>

      <C2Dialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Eliminar campaña"
        icon={DeleteOutlineRoundedIcon}
        maxWidth={440}
        actions={
          <>
            <C2SecondaryButton onClick={() => setDeleting(null)}>Cancelar</C2SecondaryButton>
            <C2DangerButton onClick={confirmDelete}>Eliminar</C2DangerButton>
          </>
        }
      >
        {deleting ? (
          <Box>
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.9rem', color: C2.dark, mb: 1 }}>
              ¿Eliminar la campaña <b>{deleting.name}</b>? Aún no tiene beneficiarios.
            </Typography>
            <Row label="Saldo por conductor" value={formatCop(deleting.amount)} />
          </Box>
        ) : null}
      </C2Dialog>
    </>
  );
}
